import { I18n } from '@aws-amplify/core';
import { Form, message, Button } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import Api from '@/services/api';
import { CreateForm } from './CreateForm';
import { CreateModal } from './CreateModal';
import { ListItens, EmptyList } from './List';
import { useApplicationContext } from '@/context/Application';

interface CompanyProps {
	data: any;
	visible: boolean;
	filtered: boolean;
	specialPlan?: boolean;
	onSelect: (company: any) => void;
	onClose: () => void;
}

const { useForm } = Form;

export function Company({
	data,
	visible = false,
	filtered,
	specialPlan = false,
	onSelect,
	onClose
}: Readonly<CompanyProps>) {
	const [form] = useForm();
	const queryClient = useQueryClient();
	const { organization } = useApplicationContext();

	const { isLoading, mutate } = useMutation((params: any) => Api.post('/company/create', params), {
		onError: (err: any) => {
			const response = err?.response?.data?.errors[0];
			const errorMessage = `${response?.msg}: ${response?.param}`;
			message.error(I18n.get(errorMessage));
		},
		onSuccess: (company: any) => {
			queryClient.invalidateQueries(['companies']);
			onClose();
			onSelect(company.data);
			message.success(I18n.get('Industrial site created successfully'));
		}
	});

	function handleOnSubmit(): void {
		form.validateFields().then((values) => {
			mutate({
				...values,
				organization_id: organization.id!!,
				url_logo: ''
			});
		});
	}

	if (data?.length === 0 && !filtered) {
		return (
			<>
				<EmptyList
					description={I18n.get(
						'Congratulations! Now create an industrial site that belongs to this company.'
					)}
				/>
				<div style={{ padding: '10px 20px 0' }}>
					<CreateForm form={form} type="organization" />
					<Button type="primary" loading={isLoading} disabled={!specialPlan} onClick={handleOnSubmit}>
						{I18n.get('Create industrial site')}
					</Button>
				</div>
			</>
		);
	}

	return (
		<>
			<CreateModal
				form={form}
				loading={isLoading}
				type="company"
				onSubmit={handleOnSubmit}
				onCancel={onClose}
				visible={visible}
			/>
			{data?.length === 0 ? (
				<EmptyList description={I18n.get('There are no registered industrial sites')} />
			) : (
				<ListItens data={data} onAction={onSelect} />
			)}
		</>
	);
}
